import React from "react";
import { motion } from "framer-motion";
import PageShell from "../components/PageShell";
import PageHero from "../components/PageHero";
import CallToAction from "../components/CallToAction";

const VALUES = [
  {
    n: "01",
    t: "Ship weekly.",
    d: "Long roadmaps rot. We move in weekly increments with measurable wins.",
  },
  {
    n: "02",
    t: "Type-safe by default.",
    d: "Compile-time guarantees beat heroic on-call sessions. Always.",
  },
  {
    n: "03",
    t: "Observable from day one.",
    d: "If we can't see it in production, we don't ship it.",
  },
  {
    n: "04",
    t: "Security is a feature.",
    d: "Threat models, least privilege and audit trails come standard.",
  },
];

const TEAM = [
  {
    name: "Mira Kahn",
    role: "Principal · Engineering",
    bio: "Ex-Stripe, ex-Cloudflare. Builds the systems that don't fall over at 3am.",
    avatar:
      "https://images.unsplash.com/photo-1770894807442-108cc33c0a7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwzfHxwb3J0cmFpdCUyMGRhcmslMjBiYWNrZ3JvdW5kJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc4MTM2ODEyN3ww&ixlib=rb-4.1.0&q=85",
  },
  {
    name: "Daniel Okafor",
    role: "Principal · AI Systems",
    bio: "PhD in NLP. Has shipped agents that actually work in production.",
    avatar:
      "https://images.unsplash.com/photo-1765776830139-72b2184dae5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxwb3J0cmFpdCUyMGRhcmslMjBiYWNrZ3JvdW5kJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc4MTM2ODEyN3ww&ixlib=rb-4.1.0&q=85",
  },
];

const STATS = [
  { v: "47", l: "Products shipped" },
  { v: "9", l: "Countries" },
  { v: "0", l: "Audit findings" },
  { v: "100%", l: "On-time launches" },
];

export default function AboutPage() {
  return (
    <PageShell>
      <PageHero
        eyebrow="◇ About"
        title="A small studio of senior engineers."
        accent="engineers."
        kicker="Ulties is a tight team that has spent the last decade shipping software that has to work — from realtime trading floors to clinical environments."
      />

      {/* Story */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-4">
          <span className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
            ◇ Story
          </span>
          <h2 className="mt-4 font-display text-4xl md:text-5xl font-medium tracking-tight text-primary-x leading-tight">
            We optimise for the work, not the headcount.
          </h2>
        </div>
        <div className="md:col-span-8 space-y-6 text-secondary-x leading-relaxed text-lg max-w-2xl">
          <p>
            Ulties was founded in 2019 by engineers who were tired of the
            agency model — junior teams, six-month discovery phases, and
            handoffs that lose the plot.
          </p>
          <p>
            We&apos;re deliberately small. Every engagement is led by a senior
            who&apos;s built the thing we&apos;re being hired to build. No
            ramp-up bench, no <span className="text-primary-x">"resource
            allocation"</span>, no Slack purgatory.
          </p>
          <p>
            We work with founders, CTOs and VP-Eng leaders who&apos;d rather
            ship one thing well than three things slowly.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-hairline bg-surface">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
          {STATS.map((s, i) => (
            <motion.div
              key={s.l}
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.07, duration: 0.6 }}
            >
              <div className="font-display text-5xl md:text-6xl font-bold tracking-[-0.03em] text-primary-x">
                {s.v}
              </div>
              <div className="mt-2 text-[11px] tracking-[0.22em] uppercase text-muted-x font-mono-x">
                {s.l}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="mb-16">
          <span className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
            ◇ Operating principles
          </span>
          <h2 className="mt-4 font-display text-5xl md:text-7xl font-medium tracking-tight text-primary-x leading-[0.95]">
            Four rules we don&apos;t break.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {VALUES.map((v, i) => (
            <motion.div
              key={v.n}
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: i * 0.07, duration: 0.7 }}
              className="rounded-2xl border border-hairline bg-glass p-8 md:p-10"
            >
              <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x mb-6">
                Rule {v.n}
              </div>
              <h3 className="font-display text-3xl md:text-4xl font-medium tracking-tight text-primary-x">
                {v.t}
              </h3>
              <p className="mt-4 text-secondary-x leading-relaxed">{v.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Team */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-28 md:pb-40">
        <div className="mb-16">
          <span className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
            ◇ Team
          </span>
          <h2 className="mt-4 font-display text-5xl md:text-7xl font-medium tracking-tight text-primary-x leading-[0.95]">
            The senior bench.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6 md:gap-10">
          {TEAM.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: i * 0.08, duration: 0.7 }}
              className="group relative overflow-hidden rounded-2xl border border-hairline bg-surface-2"
            >
              <div className="aspect-[5/4] overflow-hidden">
                <img
                  src={p.avatar}
                  alt={p.name}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                  loading="lazy"
                />
              </div>
              <div className="p-6 md:p-8">
                <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
                  {p.role}
                </div>
                <h3 className="mt-2 font-display text-2xl md:text-3xl font-medium tracking-tight text-primary-x">
                  {p.name}
                </h3>
                <p className="mt-3 text-secondary-x leading-relaxed">{p.bio}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <CallToAction
        eyebrow="◇ Work with us"
        title="Bring us your hardest one."
        body="We respond to every brief within 24 hours."
      />
    </PageShell>
  );
}
